import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import { api } from '@/utils/api'
import type { User, LoginCredentials, StorePublic } from '@/types/auth'

// 创建一个不带拦截器的 axios 实例用于刷新 token
const refreshApi = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

export const useAuthStore = defineStore('auth', () => {
  // 状态
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const isLoading = ref(false)
  const currentStore = ref<StorePublic | null>(null)

  // 计算属性
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isSuperuser = computed(() => user.value?.is_superuser === true)
  const hasRefreshToken = computed(() => !!refreshToken.value)
  const storeId = computed(() => user.value?.store_id)
  const storeName = computed(() => user.value?.store_name)

  // 方法
  const login = async (credentials: LoginCredentials) => {
    try {
      isLoading.value = true
      
      const response = await api.post('/auth/login', credentials)
      const { user: userData, access_token, refresh_token } = response.data
      
      // 保存认证信息
      user.value = userData
      token.value = access_token
      refreshToken.value = refresh_token
      
      // 保存到本地存储
      saveAuthData()
      
      return { success: true }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error?.message || '登录失败'
      }
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    try {
      if (token.value) {
        await api.post('/auth/logout')
      }
    } catch (error) {
      console.warn('Logout request failed:', error)
    } finally {
      // 清除认证信息
      user.value = null
      token.value = null
      refreshToken.value = null
      
      // 清除本地存储
      clearAuthData()
    }
  }

  const refreshAccessToken = async () => {
    try {
      if (!refreshToken.value) {
        throw new Error('No refresh token available')
      }
      
      // 使用不带拦截器的 axios 实例，并在 header 中传递 refresh token
      const response = await refreshApi.post(
        '/auth/refresh',
        {},
        {
          headers: {
            Authorization: `Bearer ${refreshToken.value}`
          }
        }
      )
      
      const { access_token } = response.data
      token.value = access_token
      
      // 更新本地存储
      saveAuthData()
      
      return true
    } catch (error) {
      console.error('Token refresh failed:', error)
      // 不在这里调用 logout，让调用方处理
      return false
    }
  }

  const checkAuth = async () => {
    try {
      if (!token.value) {
        return false
      }
      
      const response = await api.get('/auth/me')
      user.value = response.data
      
      return true
    } catch (error) {
      console.warn('Auth check failed:', error)
      await logout()
      return false
    }
  }

  const updateProfile = async (profileData: Partial<User>) => {
    try {
      isLoading.value = true
      
      const response = await api.put('/auth/profile', profileData)
      user.value = { ...user.value, ...response.data }
      
      // 更新本地存储
      saveAuthData()
      
      return { success: true }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error?.message || '更新失败'
      }
    } finally {
      isLoading.value = false
    }
  }

  const changePassword = async (passwordData: {
    currentPassword: string
    newPassword: string
  }) => {
    try {
      isLoading.value = true
      
      await api.put('/auth/password', passwordData)
      
      return { success: true }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error?.message || '密码修改失败'
      }
    } finally {
      isLoading.value = false
    }
  }

  const setAuth = (userData: User, accessToken: string, refreshTokenValue: string) => {
    user.value = userData
    token.value = accessToken
    refreshToken.value = refreshTokenValue
    saveAuthData()
  }

  const loadStoreByCode = async (storeCode: string) => {
    try {
      const response = await api.get(`/stores/public/${storeCode}`)
      currentStore.value = response.data
      localStorage.setItem('current-store', JSON.stringify(response.data))
      return response.data
    } catch (error) {
      console.warn('Failed to load store:', error)
      currentStore.value = null
      localStorage.removeItem('current-store')
      return null
    }
  }

  const clearCurrentStore = () => {
    currentStore.value = null
    localStorage.removeItem('current-store')
  }

  const saveAuthData = () => {
    if (token.value) {
      localStorage.setItem('auth-token', token.value)
    }
    if (refreshToken.value) {
      localStorage.setItem('refresh-token', refreshToken.value)
    }
    if (user.value) {
      localStorage.setItem('user-data', JSON.stringify(user.value))
    }
  }

  const loadAuthData = () => {
    try {
      const savedToken = localStorage.getItem('auth-token')
      const savedRefreshToken = localStorage.getItem('refresh-token')
      const savedUser = localStorage.getItem('user-data')
      const savedStore = localStorage.getItem('current-store')
      
      if (savedToken && savedUser) {
        token.value = savedToken
        refreshToken.value = savedRefreshToken
        user.value = JSON.parse(savedUser)
      }
      
      if (savedStore) {
        currentStore.value = JSON.parse(savedStore)
      }
      
      return !!savedToken && !!savedUser
    } catch (error) {
      console.warn('Failed to load auth data:', error)
      clearAuthData()
    }
    return false
  }

  const clearAuthData = () => {
    localStorage.removeItem('auth-token')
    localStorage.removeItem('refresh-token')
    localStorage.removeItem('user-data')
  }

  // 初始化时加载认证数据
  loadAuthData()

  return {
    // 状态
    user,
    token,
    refreshToken,
    isLoading,
    currentStore,
    
    // 计算属性
    isAuthenticated,
    isAdmin,
    isSuperuser,
    hasRefreshToken,
    storeId,
    storeName,
    
    // 方法
    login,
    logout,
    refreshAccessToken,
    checkAuth,
    updateProfile,
    changePassword,
    setAuth,
    loadStoreByCode,
    clearCurrentStore
  }
})